import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../utils/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Wallet, Building2, CreditCard, Trash2, ArrowRightLeft, ArrowUpRight, ArrowDownLeft } from "lucide-react";



const TransactionHistoryModal = ({ 
  open, 
  onClose, 
  accountType, // "cash", "bank", or "credit_card"
  account, // account object
  onDataRefresh // callback to refresh parent data
}) => {
  const [transactions, setTransactions] = useState({ 
    collections: [], 
    payments: [], 
    expenses: [], 
    transfers: [], 
    ccPayments: [] 
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && account) {
      fetchTransactions();
    }
  }, [open, account]);

  const fetchTransactions = async () => {
    if (!account) return;
    
    setLoading(true);
    try {
      if (accountType === "credit_card") {
        // For credit cards: fetch payments and expenses only
        const [paymentsRes, expensesRes] = await Promise.all([
          axios.get(`${API}/credit-card-payments`),
          axios.get(`${API}/expenses`),
        ]);
        setTransactions({
          collections: [],
          payments: [],
          expenses: expensesRes.data.filter(e => e.payment_method === "credit_card" && e.account_id === account.id),
          transfers: [],
          ccPayments: paymentsRes.data.filter(p => p.credit_card_id === account.id),
        });
      } else {
        // For cash and bank: fetch all transaction types
        const [collectionsRes, paymentsRes, expensesRes, transfersRes, ccPaymentsRes] = await Promise.all([
          axios.get(`${API}/collections`),
          axios.get(`${API}/payments`),
          axios.get(`${API}/expenses`),
          axios.get(`${API}/account-transfers`),
          axios.get(`${API}/credit-card-payments`),
        ]);
        
        const accountMethod = accountType; // "cash" or "bank"
        setTransactions({
          collections: collectionsRes.data.filter(c => c.method === accountMethod && c.account_id === account.id),
          payments: paymentsRes.data.filter(p => p.method === accountMethod && p.account_id === account.id),
          expenses: expensesRes.data.filter(e => e.payment_method === accountMethod && e.account_id === account.id),
          transfers: transfersRes.data.filter(t => 
            (t.from_type === accountMethod && t.from_account_id === account.id) ||
            (t.to_type === accountMethod && t.to_account_id === account.id)
          ),
          ccPayments: ccPaymentsRes.data.filter(p => p.payment_source === accountMethod && p.source_account_id === account.id),
        });
      }
    } catch (error) {
      console.error("Failed to fetch transactions:", error);
      toast.error("İşlemler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (type, id) => {
    const endpoints = {
      collection: `/collections/${id}`,
      payment: `/payments/${id}`,
      expense: `/expenses/${id}`,
      transfer: `/account-transfers/${id}`,
      ccPayment: `/credit-card-payments/${id}`,
    };

    const messages = {
      collection: "Bu tahsilatı silmek istediğinizden emin misiniz?",
      payment: "Bu ödemeyi silmek istediğinizden emin misiniz?",
      expense: "Bu gideri silmek istediğinizden emin misiniz?",
      transfer: "Bu transferi silmek istediğinizden emin misiniz?",
      ccPayment: "Bu kredi kartı ödemesini silmek istediğinizden emin misiniz?",
    };

    if (!window.confirm(messages[type])) return;

    try {
      await axios.delete(`${API}${endpoints[type]}`);
      toast.success("Silindi");
      fetchTransactions();
      if (onDataRefresh) {
        onDataRefresh();
      }
    } catch (error) {
      console.error(`Failed to delete ${type}:`, error);
      toast.error("Silme başarısız");
    }
  };

  const getIcon = () => {
    switch (accountType) {
      case "cash":
        return <Wallet className="h-5 w-5 text-emerald-600" />;
      case "bank":
        return <Building2 className="h-5 w-5 text-blue-600" />;
      case "credit_card":
        return <CreditCard className="h-5 w-5 text-purple-600" />;
      default:
        return null;
    }
  };

  const getBalanceColor = () => {
    if (accountType === "credit_card") {
      return "text-red-600";
    }
    return account?.balance >= 0 ? "text-emerald-600" : "text-red-600";
  };

  const getBalanceLabel = () => {
    if (accountType === "credit_card") {
      return "Mevcut Kart Borcu:";
    }
    return "Mevcut Bakiye:";
  };

  const getBalanceValue = () => {
    if (accountType === "credit_card") {
      return account?.current_debt || 0;
    }
    return account?.balance || 0;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {getIcon()}
            {account?.name} - İşlem Geçmişi
          </DialogTitle>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Tahsilatlar - Only for cash and bank */}
            {accountType !== "credit_card" && (
              <div>
                <h3 className="font-semibold text-emerald-600 mb-2 flex items-center gap-2">
                  <ArrowDownLeft className="h-4 w-4" /> Tahsilatlar (Giriş)
                </h3>
                {transactions.collections.length > 0 ? (
                  <div className="space-y-2">
                    {transactions.collections.map((c) => (
                      <div key={c.id} className="flex items-center justify-between p-2 bg-emerald-50 rounded-lg">
                        <div>
                          <span className="text-sm font-medium">{c.date}</span>
                          <span className="text-sm text-slate-600 ml-2">{c.customer_name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-emerald-600">
                            +₺{c.amount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                          </span>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleDelete("collection", c.id)} 
                            className="text-red-600 hover:bg-red-100"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">Tahsilat yok</p>
                )}
              </div>
            )}

            {/* Ödemeler - Only for cash and bank */}
            {accountType !== "credit_card" && (
              <div>
                <h3 className="font-semibold text-red-600 mb-2 flex items-center gap-2">
                  <ArrowUpRight className="h-4 w-4" /> Ödemeler (Çıkış)
                </h3>
                {transactions.payments.length > 0 ? (
                  <div className="space-y-2">
                    {transactions.payments.map((p) => (
                      <div key={p.id} className="flex items-center justify-between p-2 bg-red-50 rounded-lg">
                        <div>
                          <span className="text-sm font-medium">{p.date}</span>
                          <span className="text-sm text-slate-600 ml-2">{p.supplier_name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-red-600">
                            -₺{p.amount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                          </span>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleDelete("payment", p.id)} 
                            className="text-red-600 hover:bg-red-100"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">Ödeme yok</p>
                )}
              </div>
            )}

            {/* Giderler */}
            <div>
              <h3 className="font-semibold text-orange-600 mb-2 flex items-center gap-2">
                <ArrowUpRight className="h-4 w-4" /> {accountType === "credit_card" ? "Borçlanmalar (Giderler)" : "Giderler (Çıkış)"}
              </h3>
              {transactions.expenses.length > 0 ? (
                <div className="space-y-2">
                  {transactions.expenses.map((e) => (
                    <div key={e.id} className="flex items-center justify-between p-2 bg-orange-50 rounded-lg">
                      <div>
                        <span className="text-sm font-medium">{e.date}</span>
                        <span className="text-sm text-slate-600 ml-2">{e.category} - {e.description}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-orange-600">
                          {accountType === "credit_card" ? "" : "-"}₺{e.amount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                        </span>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleDelete("expense", e.id)} 
                          className="text-red-600 hover:bg-red-100"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">Gider yok</p>
              )}
            </div>

            {/* Transferler - Only for cash and bank */}
            {accountType !== "credit_card" && (
              <div>
                <h3 className="font-semibold text-blue-600 mb-2 flex items-center gap-2">
                  <ArrowRightLeft className="h-4 w-4" /> Transferler
                </h3>
                {transactions.transfers.length > 0 ? (
                  <div className="space-y-2">
                    {transactions.transfers.map((t) => {
                      const isOutgoing = t.from_type === accountType && t.from_account_id === account?.id;
                      return (
                        <div 
                          key={t.id} 
                          className={`flex items-center justify-between p-2 ${isOutgoing ? 'bg-red-50' : 'bg-emerald-50'} rounded-lg`}
                        >
                          <div>
                            <span className="text-sm font-medium">{t.date}</span>
                            <span className="text-sm text-slate-600 ml-2">
                              {isOutgoing ? `→ ${t.to_account_name}` : `← ${t.from_account_name}`}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`font-semibold ${isOutgoing ? 'text-red-600' : 'text-emerald-600'}`}>
                              {isOutgoing ? '-' : '+'}₺{t.amount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                            </span>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleDelete("transfer", t.id)} 
                              className="text-red-600 hover:bg-red-100"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">Transfer yok</p>
                )}
              </div>
            )}

            {/* Kredi Kartı Ödemeleri */}
            <div>
              <h3 className="font-semibold text-purple-600 mb-2 flex items-center gap-2">
                <ArrowUpRight className="h-4 w-4" /> {accountType === "credit_card" ? "Kart Borç Ödemeleri" : "Kredi Kartı Ödemeleri (Çıkış)"}
              </h3>
              {transactions.ccPayments.length > 0 ? (
                <div className="space-y-2">
                  {transactions.ccPayments.map((p) => (
                    <div key={p.id} className="flex items-center justify-between p-2 bg-purple-50 rounded-lg">
                      <div>
                        <span className="text-sm font-medium">{p.date}</span>
                        <span className="text-sm text-slate-600 ml-2">
                          {accountType === "credit_card" ? p.description : p.credit_card_name}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-purple-600">
                          {accountType === "credit_card" ? "" : "-"}₺{p.amount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                        </span>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleDelete("ccPayment", p.id)} 
                          className="text-red-600 hover:bg-red-100"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">
                  {accountType === "credit_card" ? "Ödeme yok" : "Kredi kartı ödemesi yok"}
                </p>
              )}
            </div>

            {/* Özet */}
            {account && (
              <div className="p-4 bg-slate-100 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-slate-700">{getBalanceLabel()}</span>
                  <span className={`text-xl font-bold tabular-nums ${getBalanceColor()}`}>
                    ₺{getBalanceValue().toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TransactionHistoryModal;
